import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "../../scss/common/common.module.scss";
import AppButton from "../theme/Button";
import LoadingBars from "./LoadingBars";
import BundleItem from "./bundleItem";
import { Decrypt, Encrypt } from "../utils/Functions";
import {
  getCustomQuestionLabel,
  getStoreCustomInformations,
  type CartDisplayAttachedSingle,
} from "../utils/cartLineHelpers";
const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1280,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  height: "70vh",
  overflow: "auto",
  maxWidth: "100%",
  border: "1px solid #E6E8ED",
  borderRadius: "8px",
};
interface IProps {
  cart: any;
  cartIndex: number;
  open: any;
  handleOpen: any;
  handleClose: any;
  title: any;
  subtitle: any;
  size: any;
  price: any;
  checkoutPage?: boolean;
  productLoading?: boolean;
  attachedSingles?: CartDisplayAttachedSingle[];
  bundleDisplayLabel?: string;
}

type BundleCheckoutInfo = {
  firstName?: string;
  lastName?: string;
  teamId?: string;
  jerseyNumber?: string;
  grad_year?: string;
  last_name?: string;
  misc?: string;
  customAnswers?: { id: number | string; value?: any }[];
};

function displayValue(raw: string | undefined | null): string {
  if (raw == null) return "—";
  const s = String(raw).trim();
  return s !== "" ? s : "—";
}

/** Yes/No custom questions store "1"/"0"; show human-readable labels in View Bundle. */
function displayCustomAnswerValue(
  raw: any,
  inputType: number | string | undefined | null
): string {
  if (Number(inputType) === 4) {
    if (raw == null || String(raw).trim() === "") return "—";
    const normalized = String(raw).trim().toLowerCase();
    return normalized === "1" || normalized === "true" || normalized === "yes"
      ? "Yes"
      : "No";
  }
  return displayValue(raw);
}

function teamNameFromId(storeDetail: any, teamId: string | undefined): string {
  if (teamId == null || String(teamId).trim() === "") return "—";
  const teams = storeDetail?.teams;
  if (!Array.isArray(teams)) return String(teamId);
  const t = teams.find((x: any) => String(x.id) === String(teamId));
  return t?.team_name != null ? String(t.team_name) : String(teamId);
}

function customerInfoSummaryRows(
  info: BundleCheckoutInfo | null | undefined,
  storeDetail: any
): { label: string; value: string }[] {
  if (!info) return [];

  const rows: { label: string; value: string }[] = [];
  rows.push({
    label: "Player First Name",
    value: displayValue(info.firstName),
  });
  rows.push({
    label: "Player Last Name",
    value: displayValue(info.lastName),
  });

  if (
    storeDetail?.is_team_available === 1 ||
    displayValue(info.teamId) !== "—"
  ) {
    rows.push({
      label: "Select Team",
      value: teamNameFromId(storeDetail, info.teamId),
    });
  }

  if (
    storeDetail?.is_jersey_number_required === 1 ||
    displayValue(info.jerseyNumber) !== "—"
  ) {
    rows.push({
      label: storeDetail?.jersey_number_question ?? "Jersey Number",
      value: displayValue(info.jerseyNumber),
    });
  }

  if (
    storeDetail?.is_grad_year_required === 1 ||
    displayValue(info.grad_year) !== "—"
  ) {
    rows.push({
      label: storeDetail?.grad_year_question ?? "Grad Year",
      value: displayValue(info.grad_year),
    });
  }

  if (
    storeDetail?.is_last_name_required === 1 ||
    displayValue(info.last_name) !== "—"
  ) {
    rows.push({
      label: storeDetail?.last_name_question ?? "Last name",
      value: displayValue(info.last_name),
    });
  }

  if (
    storeDetail?.is_misc_required === 1 ||
    displayValue(info.misc) !== "—"
  ) {
    rows.push({
      label: storeDetail?.misc_question ?? "Misc",
      value: displayValue(info.misc),
    });
  }

  const customInformations = getStoreCustomInformations(storeDetail);
  const customAnswers = Array.isArray(info.customAnswers)
    ? info.customAnswers
    : [];
  customInformations.forEach((question: any) => {
    const answer = customAnswers.find(
      (item) => String(item.id) === String(question.id)
    );
    rows.push({
      label: getCustomQuestionLabel(question),
      value: displayCustomAnswerValue(
        answer?.value,
        question?.input_type ?? question?.question_detail?.input_type
      ),
    });
  });

  return rows;
}
const BundleItemModal = ({
  cart,
  cartIndex,
  open,
  handleOpen,
  handleClose,
  title,
  subtitle,
  size,
  price,
  checkoutPage = false,
  productLoading,
  attachedSingles = [],
  bundleDisplayLabel = "",
}: IProps) => {
  const storeData = useSelector((state: any) => state.storeDataSlice.data);
  const customerInfoRows = useMemo(
    () =>
      cart && !cart.isSingleProduct
        ? customerInfoSummaryRows(cart.bundle_checkout_info, storeData)
        : [],
    [cart, storeData]
  );
  const [bundleErros, setBundleErros] = useState<number[]>([]);
  const changeQuantity = (productId: number, quantity: any) => {
    if (!cart?.isSingleProduct) {
      const product = cart?.bundle_products.findIndex(
        (product: any) => product.id === productId
      );
      if (product >= 0) {
        cart.bundle_products[product].store_product_detail.quantity = quantity;
      }
    } else {
      if (cart?.id === productId) {
        cart.quantity = quantity;
      }
    }
  };

  const editOptionValue = (
    productId: number,
    optionId: number,
    selectedValue: any
  ) => {
    console.log(productId, "productId, cartDetail");
    if (!cart?.isSingleProduct) {
      console.log("bundle", productId, cart, optionId);

      const product = cart?.bundle_products.findIndex(
        (product: any) => product.id === productId
      );
      if (product >= 0) {
        const optionIndex = cart?.bundle_products[product]?.store_product_detail?.store_options?.findIndex(
          (option: any) => option.id === optionId
        );
        if (optionIndex >= 0 && cart?.bundle_products[product]?.store_product_detail?.store_options) {
          cart.bundle_products[product].store_product_detail.store_options[optionIndex].selectedData = selectedValue;
        }
      }
    }
  };
  const saveChanges = () => {
    const cartDataInfoD = localStorage?.getItem("cart");
    const cartDataInfo = Decrypt(cartDataInfoD);
    const cartOldData = cartDataInfo ? JSON.parse(cartDataInfo) : [];

    if (!cartOldData?.length) {
      handleClose();
      return;
    }

    const newCartData = [...cartOldData];
    newCartData[cartIndex] = cart;
    attachedSingles.forEach(({ index, item }) => {
      if (index >= 0 && index < newCartData.length) {
        newCartData[index] = item;
      }
    });
    const cartDataEn = Encrypt(JSON.stringify(newCartData));
    localStorage.setItem("cart", cartDataEn);
    handleClose();
  };

  const removeAttachedLine = (lineIndex: number) => {
    const cartDataInfoD = localStorage?.getItem("cart");
    const cartDataInfo = Decrypt(cartDataInfoD);
    const cartOldData = cartDataInfo ? JSON.parse(cartDataInfo) : [];
    if (lineIndex < 0 || lineIndex >= cartOldData.length) {
      handleClose();
      return;
    }
    cartOldData.splice(lineIndex, 1);
    localStorage.setItem("cart", Encrypt(JSON.stringify(cartOldData)));
    handleClose();
  };
  return (
    <div>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
          setBundleErros([]);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className={styles.shoppingItems}>

          <h3 style={{ textAlign: "center" }}>
            {bundleDisplayLabel || (cart?.isSingleProduct ? cart?.product_name : "Bundle")}
          </h3>
          <p style={{ textAlign: "center", marginTop:"4px", fontSize:"14px" }}>
            {cart?.description?.replace(/<[^>]*>/g, "")}
          </p>
          <div className={styles.bundleModal_heading}>
          <div>
          <Typography
              className={styles.modalTitle}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              {!cart?.isSingleProduct
                ? `Bundle Items (${cart &&
                cart?.bundle_products &&
                cart?.bundle_products.length + attachedSingles.length
                })`
                : `Item (1)`}
            </Typography>
            {!productLoading && cart && !cart?.isSingleProduct ? (
            <Box
              sx={{
                // mt: 3,
                pt: 1,
                borderTop: "1px solid #E6E8ED",
              }}
            >
              {/* <Typography
                variant="subtitle1"
                component="h2"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Bundle details:
              </Typography> */}

              {!cart?.bundle_checkout_info ? (
                <Typography variant="body2" color="text.secondary">
                  Not found.
                </Typography>
              ) : (
                customerInfoRows.map((row, j) => (
                  <Typography key={`${row.label}-${j}`} variant="body2" sx={{ mb: 0.75 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Box>
                    {": "}
                    {row.value}
                  </Typography>
                ))
              )}
            </Box>
          ) : null}
          </div>

            <div className={styles.bundleModal_btns}>
              {checkoutPage ? (
                <AppButton
                  className={styles.itemModalBtnClose}
                  onClick={handleClose}
                >
                  Close
                </AppButton>
              ) : (
                <>
                  <AppButton
                    className={styles.itemModalBtnCencel}
                    onClick={handleClose}
                  >
                    Cancel
                  </AppButton>
                  <AppButton
                    className={styles.itemModalBtnSave}
                    onClick={saveChanges}
                    disabled={bundleErros?.length > 0}
                  >
                    Save Changes
                  </AppButton>
                </>
              )}
            </div>
          </div>

          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            {productLoading ? (
              <LoadingBars />
            ) : (
              <div className={styles.itemModal}>
                {cart &&
                  !cart?.isSingleProduct &&
                  cart?.bundle_products &&
                  cart?.bundle_products.map((item: any, index: number) => (
                    <BundleItem
                      key={index}
                      product={item}
                      changeQuantity={changeQuantity}
                      checkoutPage={checkoutPage}
                      bundleErros={bundleErros}
                      setBundleErros={setBundleErros}
                      editOptionValue={editOptionValue}
                    />
                  ))}
                {cart &&
                  !cart?.isSingleProduct &&
                  attachedSingles.map(({ index: lineIndex, item }) => (
                    <div
                      key={`attached-single-${item?.cartItemId ?? lineIndex}-${item?.id}`}
                      style={{ width: "100%", marginBottom: "12px" }}
                    >
                      <BundleItem
                        product={item}
                        changeQuantity={(_productId: number, quantity: number) => {
                          item.quantity = quantity;
                        }}
                        checkoutPage={checkoutPage}
                        bundleErros={bundleErros}
                        setBundleErros={setBundleErros}
                        editOptionValue={(
                          _productId: number,
                          optionId: number,
                          selectedValue: any
                        ) => {
                          const optionIndex = item.store_options?.findIndex(
                            (option: any) => option.id === optionId
                          );
                          if (
                            optionIndex != null &&
                            optionIndex >= 0 &&
                            item.store_options
                          ) {
                            item.store_options[optionIndex].selectedData =
                              selectedValue;
                          }
                        }}
                      />
                      {/* {!checkoutPage ? (
                        <div style={{ width: "100%", marginTop: 8 }}>
                          <AppButton
                            type="button"
                            className={styles.itemModalBtnCencel}
                            onClick={() => removeAttachedLine(lineIndex)}
                          >
                            Remove from cart
                          </AppButton>
                        </div>
                      ) : null} */}
                    </div>
                  ))}
                {cart && cart?.isSingleProduct && (
                  <BundleItem
                    product={cart}
                    changeQuantity={changeQuantity}
                    checkoutPage={checkoutPage}
                    bundleErros={bundleErros}
                    setBundleErros={setBundleErros}
                    editOptionValue={editOptionValue}
                  />
                )}
              </div>
            )}
          </Typography>

          {/* {!productLoading && cart && !cart?.isSingleProduct ? (
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: "1px solid #E6E8ED",
              }}
            >
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{ fontWeight: 600, mb: 0.5 }}
              >
                Bundle details:
              </Typography>

              {!cart?.bundle_checkout_info ? (
                <Typography variant="body2" color="text.secondary">
                  Not found.
                </Typography>
              ) : (
                customerInfoRows.map((row, j) => (
                  <Typography key={`${row.label}-${j}`} variant="body2" sx={{ mb: 0.75 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {row.label}
                    </Box>
                    {": "}
                    {row.value}
                  </Typography>
                ))
              )}
            </Box>
          ) : null} */}
        </Box>
      </Modal>
    </div>
  );
};

export default BundleItemModal;
